import type { CloudAPI } from '@yandex-360/telemost-sdk/cloud-api';
import { merge } from 'lodash';
import { batchActions } from 'redux-batched-actions';
import { all, call, put, select, takeEvery, takeLatest } from 'typed-redux-saga';

import { selectHasConferencePermissionsEditMatrixPermission } from '../../../pages/call/features/permissions';
import { getConferenceConfig } from '../../../pages/call/selectors';
import { createLogger } from '../../helpers/loggerFacade';
import { Role } from '../../helpers/permissions';
import { cloudApi } from '../../helpers/transport';
import { closeSettingsModal } from '../settings-modal/slice';
import {
    selectPermissionsExtendedMatrix,
    selectPermissionsExtendedMatrixOverride,
    selectPermissionsExtendedStatus,
} from './selectors';
import {
    loadPermissionsExtendedMatrixAction,
    resetPermissionsExtendedMatrix,
    resetPermissionsExtendedMatrixOverride,
    resetPermissionsExtendedStatus,
    setNonConfigurablePermissions,
    setPermissionExtendedHaveChangedFromDefault,
    setPermissionExtendedPresetId,
    setPermissionsExtendedMatrix,
    setPermissionsExtendedStatus,
} from './slice';
import { PermissionsExtendedStatus } from './types';
import { convertRolesToMatrix, getMatrixDiffRoles } from './utils';

const logger = createLogger('permissions-extended');

function* loadPermissionsExtendedMatrixSaga() {
    const config = yield* select(getConferenceConfig);
    const uri = config?.resolved_uri;

    if (!uri) {
        logger.error('No conference uri', {
            method: 'loadPermissionsExtendedMatrixSaga',
        });
        return;
    }

    const status = yield* select(selectPermissionsExtendedStatus);
    if (!status || status === PermissionsExtendedStatus.INIT_ERROR) {
        yield* put(setPermissionsExtendedStatus(PermissionsExtendedStatus.MATRIX_LOADING));
    }
    try {
        const { roles, preset_id, non_configurable_permissions, role_permissions_have_changed_from_default } =
            yield* call([cloudApi, cloudApi.getMatrix], uri);

        const matrix = convertRolesToMatrix(roles);

        yield* put(
            batchActions([
                setPermissionsExtendedMatrix(matrix),
                setPermissionExtendedPresetId(preset_id),
                setPermissionExtendedHaveChangedFromDefault(role_permissions_have_changed_from_default),
                setNonConfigurablePermissions(non_configurable_permissions),
                setPermissionsExtendedStatus(PermissionsExtendedStatus.FULLFILLED),
            ])
        );
    } catch (error) {
        logger.error(error, {
            method: 'loadPermissionsExtendedMatrixSaga',
        });
        const status = yield* select(selectPermissionsExtendedStatus);
        if (status === PermissionsExtendedStatus.MATRIX_LOADING) {
            yield* put(setPermissionsExtendedStatus(PermissionsExtendedStatus.INIT_ERROR));
        }
    }
}

function* sendResetToDefaultPermissionsExtended(role?: Role) {
    const config = yield* select(getConferenceConfig);
    const uri = config?.resolved_uri;

    try {
        if (!uri) {
            throw new Error('No conference uri');
        }
        yield* call([cloudApi, cloudApi.resetAllRolePermissions], uri, role);
    } catch (error) {
        logger.error(error, {
            method: 'sendResetToDefaultPermissionsExtended',
        });
        throw error;
    }
}

function* sendSetPermissionsExtendedMatrix(payload: CloudAPI.SetMatrix.Request) {
    const config = yield* select(getConferenceConfig);
    const uri = config?.resolved_uri;

    try {
        if (!uri) {
            throw new Error('No conference uri');
        }
        yield* call([cloudApi, cloudApi.setMatrix], uri, payload);
    } catch (error) {
        logger.error(error, {
            method: 'sendSetPermissionsExtendedMatrix',
        });
        throw error;
    }
}

export function* applyPermissionExtendedMatrix() {
    const matrix = yield* select(selectPermissionsExtendedMatrix);
    const matrixOverride = yield* select(selectPermissionsExtendedMatrixOverride);

    if (!matrixOverride) {
        const error = new Error('Matrix override is null');
        logger.error(error, {
            method: 'applyPermissionExtendedMatrix',
        });
        throw error;
    }

    // Калькуляция диффа матриц
    const matrixDiff = getMatrixDiffRoles(matrix, matrixOverride);

    yield* put(setPermissionsExtendedStatus(PermissionsExtendedStatus.APPLY_MATRIX_LOADING));
    try {
        yield* call(sendSetPermissionsExtendedMatrix, {
            roles: matrixDiff,
        });
    } catch (error) {
        logger.error(error, {
            method: 'applyPermissionExtendedMatrix',
        });
        throw error;
    } finally {
        yield* put(setPermissionsExtendedStatus(PermissionsExtendedStatus.FULLFILLED));
    }

    const mergedMatrix = merge({}, matrix, matrixOverride);

    yield* put(setPermissionsExtendedMatrix(mergedMatrix));
    yield* put(resetPermissionsExtendedMatrixOverride());
}

export function* resetToDefaultPermissionsExtended() {
    yield* put(setPermissionsExtendedStatus(PermissionsExtendedStatus.RESET_TO_DEFAULT_LOADING));
    try {
        yield* call(sendResetToDefaultPermissionsExtended);
        yield* put(resetPermissionsExtendedMatrixOverride());
    } catch (error) {
        logger.error(error, {
            method: 'resetToDefaultPermissionsExtended',
        });
        throw error;
    } finally {
        yield* put(setPermissionsExtendedStatus(PermissionsExtendedStatus.FULLFILLED));
    }
}

function* safeResetPermissionsExtended() {
    const status = yield* select(selectPermissionsExtendedStatus);
    const hasConferencePermissionsEditMatrixPermission = yield* select(
        selectHasConferencePermissionsEditMatrixPermission
    );

    if (!hasConferencePermissionsEditMatrixPermission) {
        yield* put(resetPermissionsExtendedStatus());
        yield* put(resetPermissionsExtendedMatrix());
    }
    if (status !== PermissionsExtendedStatus.APPLY_MATRIX_LOADING || !hasConferencePermissionsEditMatrixPermission) {
        yield* put(resetPermissionsExtendedMatrixOverride());
    }
}

export function* permissionsExtendedSaga() {
    yield* all([
        takeLatest(loadPermissionsExtendedMatrixAction, loadPermissionsExtendedMatrixSaga),
        takeEvery(closeSettingsModal, safeResetPermissionsExtended),
    ]);
}
